import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';
import { track } from '@/state/telemetry';
import type { Remark } from '@/types/api';

interface Props {
  projectId: string;
  remark: Remark;
  onClose: () => void;
}

/** Участник проекта из GET /projects/{id}/auth-members. */
interface AuthMember {
  id: string;             // auth_users.id
  email: string;
  full_name: string;
  default_role: string;   // expert | gip | curator | admin
  expert_section: string | null;
  role: string;           // pm.role в проекте: expert | gip | curator
}

interface AuthMembersResponse {
  members: AuthMember[];
}

export function TransferRemarkModal({ projectId, remark, onClose }: Props) {
  const qc = useQueryClient();
  const [expertId, setExpertId] = useState<string>('');
  const [comment, setComment] = useState('');

  // Реальные участники проекта (см. GET /projects/{id}/auth-members в app/main.py).
  // Фильтруем только expert / gip — кому имеет смысл передавать замечание.
  const membersQ = useQuery<AuthMembersResponse>({
    queryKey: ['expertise-members', projectId],
    queryFn: () => api.get<AuthMembersResponse>(`/projects/${projectId}/auth-members`),
    retry: false,
  });

  const experts = useMemo(() => {
    const all = membersQ.data?.members || [];
    return all.filter((m) => m.role === 'expert' || m.role === 'gip');
  }, [membersQ.data]);

  // Авто-выбор первого, как только список загрузился.
  if (!expertId && experts.length > 0) {
    setExpertId(experts[0].id);
  }

  const transfer = useMutation({
    mutationFn: async () => {
      const expert = experts.find((e) => e.id === expertId);
      if (!expert) throw new Error('Эксперт не выбран');
      // Backend контракт (см. /correct в app/main.py): { field, new_value }.
      // expert_name — единственное корректируемое скалярное поле для «человекочитаемого»
      // имени ответственного. Реальный re-route по user_id появится в следующих версиях.
      return api.post(`/projects/${projectId}/remarks/${remark.id}/correct`, {
        field: 'expert_name',
        new_value: expert.full_name,
        reason: comment.trim() || `Передано: ${expert.email}`,
      });
    },
    onSuccess: () => {
      const expert = experts.find((e) => e.id === expertId);
      toast.success(`Передано: ${expert?.full_name || expert?.email}`);
      track('remark_reassigned', {
        project_id: projectId,
        target_entity_id: remark.id,
        payload: { to_user_id: expertId, to_email: expert?.email },
      });
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      qc.invalidateQueries({ queryKey: ['remarks-dashboard', projectId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось передать'),
  });

  const isLoading = membersQ.isLoading;
  const isEmpty = !isLoading && experts.length === 0;

  return (
    <Modal
      title={`Передать замечание ${remark.id.slice(0, 8)} другому эксперту`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            disabled={transfer.isPending || isEmpty || !expertId}
            onClick={() => transfer.mutate()}
          >
            {transfer.isPending ? 'Передача…' : 'Передать'}
          </Button>
        </>
      }
    >
      <p className="text-muted" style={{ fontSize: 13 }}>
        Текущий эксперт: <span className="text-ink" style={{ fontWeight: 500 }}>{remark.expert_name || remark.specialist_role || 'не назначен'}</span>
      </p>
      <div className="mt-4">
        <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Новый эксперт</label>
        {isLoading && (
          <div className="text-muted" style={{ fontSize: 13 }}>Загрузка участников…</div>
        )}
        {isEmpty && (
          <div
            className="rounded-btn"
            style={{ padding: '10px 14px', fontSize: 13, background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}
          >
            Эксперты не назначены на проект. Сначала пригласите участников через «Настройки → Участники».
          </div>
        )}
        {!isLoading && !isEmpty && (
          <select
            value={expertId}
            onChange={(e) => setExpertId(e.target.value)}
            className="w-full bg-white text-ink rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 14, border: '1px solid #E5E7EB' }}
          >
            {experts.map((m) => {
              const sec = m.expert_section ? ` · ${m.expert_section}` : '';
              const roleLabel = m.role === 'gip' ? 'ГИП' : 'эксперт';
              return (
                <option key={m.id} value={m.id}>
                  {m.full_name || m.email} ({roleLabel}{sec})
                </option>
              );
            })}
          </select>
        )}
      </div>
      <div className="mt-4">
        <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Комментарий (опц.)</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder="Например: тематика ближе к разделу ТКР"
          className="w-full bg-white text-ink rounded-btn outline-none focus:border-ink"
          style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'Inter, system-ui, sans-serif', border: '1px solid #E5E7EB' }}
        />
      </div>
      <p className="text-muted mt-3" style={{ fontSize: 12 }}>
        Эксперт получит уведомление по эл. почте. Замечание появится в его очереди.
      </p>
    </Modal>
  );
}
